import { HiCheck, HiPencil } from 'react-icons/hi';
import {
  forwardRef,
  HTMLProps,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { useSyncState } from '../../utils';
import Typography, { TypographyProps } from '../Typography';
import IconButton from '../IconButton';
import Input from '../Input';
import styles from './LabelEditor.module.scss';

export interface LabelEditorProps
  extends Omit<HTMLProps<HTMLInputElement>, 'type'> {
  textProps?: TypographyProps<any>;
  name: string;
}
function LabelEditor(
  { textProps, className, value: v, name, ...props }: LabelEditorProps,
  ref: any,
) {
  const [value, setValue] = useSyncState(v ?? props.defaultValue);
  const [canEdit, setCanEdit] = useState(false);
  const _ref = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => _ref.current);

  useEffect(() => {
    if (canEdit) return;
    return () => setValue(v ?? props.defaultValue);
  }, [canEdit, setValue, v, props.defaultValue]);

  return (
    <form
      className={cx(canEdit && styles.edit, styles.root, className)}
      onSubmit={(e) => {
        e.preventDefault();
        props.onChange?.({
          ...e,
          currentTarget: _ref.current,
          target: _ref.current,
        } as any);
        setCanEdit(false);
        return false;
      }}
    >
      {canEdit ? (
        <Input
          onChange={(e) => setValue(e.currentTarget.value)}
          className={styles.input}
          inputProps={props}
          value={value}
          name={name}
          ref={_ref}
        />
      ) : (
        <Typography
          {...textProps}
          className={cx(styles.text, textProps.className)}
        >
          {v ?? props.defaultValue}
        </Typography>
      )}
      <IconButton
        onClick={(e: any) => {
          if (!canEdit) {
            e.preventDefault();
            setCanEdit(true);
          }
        }}
        type={canEdit ? 'submit' : 'button'}
        icon={canEdit ? HiCheck : HiPencil}
        disabled={props.disabled}
        variant="ghost"
      />
    </form>
  );
}

export default forwardRef(LabelEditor);
